/**
 * FHIR QuestionnaireResponse API facade.
 *
 * Delegates CRUD to FhirApiService and adds item-level operations (get/put/patch items, get/put/patch single item by
 * linkId).
 */

import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import type { FHIRQuestionnaireResponse, FHIRQuestionnaireResponseItem } from '../models/fhir.model';
import { FhirApiService } from './fhir-api.service';
import type { FhirBundle, QuestionnaireResponseSearchParams } from './fhir-api.types';
import { findItemByLinkId, mergeItemsByLinkId, patchItemByLinkId, replaceItemByLinkId } from './fhir-item-tree.utils';

const RESOURCE_TYPE = 'QuestionnaireResponse';

@Injectable()
export class FhirQuestionnaireResponseApiService {
  private readonly fhirApi = inject(FhirApiService);

  getQuestionnaireResponse(id: string): Observable<FHIRQuestionnaireResponse> {
    return this.fhirApi.get<FHIRQuestionnaireResponse>(RESOURCE_TYPE, id);
  }

  putQuestionnaireResponse(response: FHIRQuestionnaireResponse): Observable<FHIRQuestionnaireResponse> {
    // Background save — must not raise the full-screen loader, which reads as a page reload mid-assessment.
    return this.fhirApi.put(response, { skipLoadingIndicator: true });
  }

  patchQuestionnaireResponse(
    id: string,
    partial: Partial<FHIRQuestionnaireResponse>
  ): Observable<FHIRQuestionnaireResponse> {
    return this.fhirApi.patch<FHIRQuestionnaireResponse>(RESOURCE_TYPE, id, partial, { skipLoadingIndicator: true });
  }

  createQuestionnaireResponse(response: FHIRQuestionnaireResponse): Observable<FHIRQuestionnaireResponse> {
    return this.fhirApi.create(response);
  }

  searchQuestionnaireResponses(
    params?: QuestionnaireResponseSearchParams
  ): Observable<FhirBundle<FHIRQuestionnaireResponse>> {
    return this.fhirApi.search<FHIRQuestionnaireResponse>(RESOURCE_TYPE, params);
  }

  deleteQuestionnaireResponse(id: string): Observable<void> {
    return this.fhirApi.delete(RESOURCE_TYPE, id);
  }

  // ---------- Item-level ----------

  getQuestionnaireResponseItems(responseId: string): Observable<FHIRQuestionnaireResponseItem[]> {
    return this.getQuestionnaireResponse(responseId).pipe(map((r) => r.item ?? []));
  }

  putQuestionnaireResponseItems(
    responseId: string,
    items: FHIRQuestionnaireResponseItem[]
  ): Observable<FHIRQuestionnaireResponse> {
    return this.getQuestionnaireResponse(responseId).pipe(
      switchMap((r) => this.putQuestionnaireResponse({ ...r, item: items }))
    );
  }

  patchQuestionnaireResponseItems(
    responseId: string,
    items: FHIRQuestionnaireResponseItem[]
  ): Observable<FHIRQuestionnaireResponse> {
    return this.getQuestionnaireResponse(responseId).pipe(
      switchMap((r) => {
        const merged = mergeItemsByLinkId(r.item ?? [], items);
        return this.putQuestionnaireResponse({ ...r, item: merged });
      })
    );
  }

  // ---------- Single item by linkId ----------

  getQuestionnaireResponseItem(
    responseId: string,
    linkId: string
  ): Observable<FHIRQuestionnaireResponseItem | undefined> {
    return this.getQuestionnaireResponseItems(responseId).pipe(map((items) => findItemByLinkId(items, linkId)));
  }

  putQuestionnaireResponseItem(
    responseId: string,
    linkId: string,
    item: FHIRQuestionnaireResponseItem
  ): Observable<FHIRQuestionnaireResponse> {
    return this.getQuestionnaireResponse(responseId).pipe(
      switchMap((r) => {
        const root = r.item ?? [];
        const updated = replaceItemByLinkId(root, linkId, item);
        return this.putQuestionnaireResponse({ ...r, item: updated });
      })
    );
  }

  patchQuestionnaireResponseItem(
    responseId: string,
    linkId: string,
    partial: Partial<FHIRQuestionnaireResponseItem>
  ): Observable<FHIRQuestionnaireResponse> {
    return this.getQuestionnaireResponse(responseId).pipe(
      switchMap((r) => {
        const root = r.item ?? [];
        const updated = patchItemByLinkId(root, linkId, partial);
        return this.putQuestionnaireResponse({ ...r, item: updated });
      })
    );
  }
}
